<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import CopilotScoreFeedbackModal from '@/components/Conversations/Copilot/CopilotScoreFeedbackModal.vue';
import ResizeHandle from '@/components/Shared/Divider/ResizeHandle.vue';
import ConversationIndexFilters from '@/components/Conversations/ConversationIndex/ConversationIndexFilters.vue';
import ProNotesService from '@/core/shared/pro-notes/pro-notes.service';
import ConversationIndexService from '@/core/conversations/conversation-index/conversation-index.service';
import ConversationWatcherService from '@/core/conversations/conversation-watcher/conversation-watcher.service';
import { useImmediateOutreach } from '@/core/jobs/immediate-outreach/use-immediate-outreach';
import TrackingService from '@/core/shared/tracking/tracking.service';
import ProProfileService from '@/core/conversations/pro-profile/pro-profile.service';
import MeService from '@/core/shared/me/me.service';
import SpinnerLoader from '@/components/Shared/Loaders/SpinnerLoader.vue';
import EmptyConversationIndex from '@/components/Conversations/ConversationIndex/EmptyConversationIndex.vue';
import { ErrorService } from '@/core/shared/errors/error.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import ProjectService from '@/core/shared/project/project.service';
import { DrawerService } from '@/core/shared/drawer/drawer.service';
import { LayoutService } from '@/core/shared/layout/layout.service';
import { type ATSApplicationScoringProScoring } from '@factoryfixinc/ats-interfaces';
import { DialogService } from '@/core/shared/dialog/dialog.service';
import AddProToJobModal from '@/components/Shared/Modals/AddProToJobModal.vue';
import ConversationsTabs from '@/components/Conversations/Tabs/Tabs.vue';
import DoNotContactBanner from '@/components/Conversations/DoNotContact/DoNotContactBanner.vue';
import ProHeader from '@/components/Conversations/ProHeader/ProHeader.vue';
import AIThinkingAnimation from '@/components/Common/AIThinkingAnimation.vue';
import LoadingActiveOutreachAnimation from '@/components/Common/LoadingActiveOutreachAnimation.vue';
import InterviewScheduledBanner from '@/components/Conversations/Interview/InterviewScheduledBanner.vue';
import InterviewSchedulingService from '@/core/shared/interview-scheduling/interview-scheduling.service';
import ProHeaderPlaceholder from '@/components/Conversations/ProHeader/ProHeaderPlaceholder.vue';
import CopilotScoreBlurbPlaceholder from '@/components/Conversations/Copilot/CopilotScoreBlurbPlaceholder.vue';
import ResumePageFrame from '@/components/Conversations/Resume/ResumePageFrame.vue';
import { hasApplicationScore } from '@/core/conversations/application-score/application-score.util';

const proHeaderRef = ref<InstanceType<typeof ProHeader>>();
const route = useRoute();
const interviewSchedulingService = new InterviewSchedulingService();

const dialogService = new DialogService();
const meService = new MeService();
const proNoteService = new ProNotesService();
const projectService = new ProjectService();
const drawerService = new DrawerService();
const layoutService = new LayoutService();
const conversationIndexService = new ConversationIndexService();
const doNotContact = computed(() => conversationIndexService.selectedProDoNotContact);
const conversationWatcherService = new ConversationWatcherService();
const conversationProProfileService = new ProProfileService();

// Use the immediate outreach composable
const { shouldShowAIAnimation } = useImmediateOutreach(conversationIndexService, projectService);

const conversationsPanel = ref<HTMLElement | null>(null);

const conversationsPanelWidth = computed({
  get: () => layoutService.conversationsPanelWidth,
  set: (width: number) => {
    layoutService.conversationsPanelWidth = width;
  },
});

const conversationsPanelOffsetWidth = computed(() =>
  drawerService.isProjectsDrawerOpen ? 392 : 56,
);

const addProToJobModalProps = computed({
  get: () => dialogService.addProToJobModalProps,
  set: (props) => {
    dialogService.addProToJobModalProps = props;
  },
});

const tabItems = computed(() => [
  { title: 'Resume', routeName: `job-resume-tab`, show: true },
  { title: 'Conversation', routeName: `job-conversation-tab`, show: true },
  { title: 'Applications', routeName: `job-applications-tab`, show: true },
  {
    title: 'Notes',
    routeName: `job-notes-tab`,
    show: true,
    count: isLoadingSelectedCandidate.value ? undefined : proNoteService.notesWithJobHeaders.length,
  },
]);

const currentEmployerId = computed(() => meService.employer?.id);
const selectingConversationIndex = computed(
  () => conversationIndexService.selectingConversationIndex,
);
const hasConversations = computed(() => conversationIndexService.conversationIndexes.length > 0);
// A job switch that has not landed its list yet means the selected candidate is stale.
const isAwaitingJobSwitchList = computed(
  () => conversationIndexService.isSwitchingJob && !hasConversations.value,
);
// Placeholders replace the header too: it renders from the profile, so showing it
// early would put the previous candidate's data under the new name.
const isLoadingSelectedCandidate = computed(
  () =>
    selectingConversationIndex.value ||
    conversationProProfileService.isLoadingSelectedProProfile ||
    isAwaitingJobSwitchList.value,
);
const isSelectedCandidateScored = computed(() =>
  hasApplicationScore(
    conversationIndexService.selectedConversationIndex?.primaryApplicationIndex?.score,
  ),
);
const didSelectedProProfileFail = computed(
  () => conversationProProfileService.didSelectedProProfileFail,
);
const selectedCandidateName = computed(
  () => conversationIndexService.selectedConversationIndex?.proName ?? '',
);
const isRetryingProProfile = ref(false);

async function retryProProfile() {
  isRetryingProProfile.value = true;
  try {
    await conversationProProfileService.retrySelectedProProfile();
  } finally {
    isRetryingProProfile.value = false;
  }
}
const currentRouteName = computed(() => route.name);
const isResumeTab = computed(() => currentRouteName.value === 'job-resume-tab');
const canNavigateTabs = computed(() => Boolean(route.params.projectId));

const currentConversationId = computed(
  () => conversationIndexService.selectedConversationIndex?.conversationId,
);

const nextUpcomingInterview = computed(() => interviewSchedulingService.nextUpcomingInterview);

async function handleProHeaderChangeProScore(proScore: ATSApplicationScoringProScoring) {
  const selectedConversationIndex = conversationIndexService.selectedConversationIndex ?? undefined;

  // We use the primary application index to display the pro's score in the conversation index card
  conversationIndexService.modifyConversationIndexPrimaryApplicationIndex(
    selectedConversationIndex,
    {
      applicationId: proScore.jobApplicationId,
      score: proScore.score,
    },
  );
}

function onAddProToJobModalClose() {
  addProToJobModalProps.value = { isOpen: false };
}

watch(currentRouteName, async (newRouteName) => {
  if (typeof newRouteName !== 'string') {
    return;
  }

  if (/-tab$/.test(newRouteName)) {
    drawerService.visitedConversationsTabRouteName = newRouteName;
  }

  const tabTitle = tabItems.value.find((item) => item.routeName === newRouteName);
  if (tabTitle?.title) {
    TrackingService.trackAction(`${tabTitle.title} Tab Viewed`, {
      pro_id: conversationProProfileService.selectedProProfile?.id,
      conversation_id: conversationIndexService.selectedConversationIndex?.conversationId,
      application_id: conversationProProfileService.selectedProApplication?.id,
    });
  }
});

// Fetch scheduled events when conversation changes
watch(
  currentConversationId,
  async (newConversationId, oldConversationId) => {
    if (newConversationId && newConversationId !== oldConversationId) {
      // Fetch interview scheduling events for the banner
      if (currentEmployerId.value) {
        await interviewSchedulingService.fetchScheduledEvents(
          currentEmployerId.value,
          newConversationId,
        );
      }
    } else if (!newConversationId) {
      interviewSchedulingService.clearScheduledEvents();
    }
  },
  { immediate: true },
);

onMounted(async () => {
  try {
    if (currentEmployerId.value) {
      await conversationWatcherService.fetchConversationWatchers(currentEmployerId.value);
    }
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Failed to fetch available jobs');
    throw error;
  }
});
</script>

<template>
  <div class="flex h-screen w-full">
    <!-- Empty Conversations Panel -->
    <div
      v-if="projectService.areNoProjectsAvailable"
      class="flex h-full w-full items-center justify-center"
    >
      <EmptyConversationIndex />
    </div>

    <!-- Loading Conversations Panel -->
    <div
      v-else-if="!projectService.didLoadProjects"
      class="flex h-full w-full items-center justify-center"
    >
      <SpinnerLoader />
    </div>

    <div v-else class="flex h-full w-full">
      <!-- Middle Conversations Panel  -->
      <section
        ref="conversationsPanel"
        class="overflow-hidden px-2"
        :style="`width: ${conversationsPanelWidth}px`"
      >
        <ConversationIndexFilters />
      </section>

      <ResizeHandle
        :element="conversationsPanel"
        :min-element-width="240"
        :max-element-width="480"
        :element-offset-width="conversationsPanelOffsetWidth"
        @change:element-width="conversationsPanelWidth = $event"
      />

      <!-- Right Panel with Router View -->
      <section class="h-full w-full flex-1 overflow-hidden">
        <template v-if="isAwaitingJobSwitchList">
          <div class="h-full overflow-hidden p-4" data-test="pro-profile-skeleton">
            <v-skeleton-loader boilerplate type="article, subtitle, article, article, paragraph" />
          </div>
        </template>

        <template v-else-if="isLoadingSelectedCandidate">
          <div class="flex h-full flex-col overflow-hidden">
            <div
              class="flex min-h-[95px] shrink-0 flex-col justify-between border-b border-b-tint-60"
            >
              <ProHeaderPlaceholder />
              <ConversationsTabs :items="tabItems" :disabled="!canNavigateTabs" />
            </div>
            <div class="min-h-0 flex-1 overflow-auto">
              <div v-if="isResumeTab" class="h-full w-full overflow-y-scroll p-4">
                <CopilotScoreBlurbPlaceholder v-if="isSelectedCandidateScored" />
                <ResumePageFrame loading />
              </div>
              <div v-else class="flex h-full items-center justify-center">
                <SpinnerLoader />
              </div>
            </div>
          </div>
        </template>

        <template v-else-if="didSelectedProProfileFail">
          <div
            class="flex h-full flex-col items-center justify-center px-6 text-center"
            data-test="pro-profile-error"
          >
            <p class="mb-2 text-sm font-bold text-shade-800">
              We couldn't load this candidate's profile
            </p>
            <p class="mb-4 max-w-80 text-sm text-shade-800">
              Nothing was sent or changed. Try again to open
              {{ selectedCandidateName || 'this candidate' }}.
            </p>
            <v-btn
              variant="outlined"
              :loading="isRetryingProProfile"
              data-test="pro-profile-retry"
              @click="retryProProfile"
            >
              Retry
            </v-btn>
          </div>
        </template>

        <template v-else>
          <!-- Show AI Animation when conditions are met -->
          <div v-if="shouldShowAIAnimation" class="flex h-full w-full items-center justify-center">
            <div class="flex flex-col">
              <div class="ml-4 self-start">
                <AIThinkingAnimation :width="'24px'" :height="'24px'" :text-cycle-duration="2500" />
              </div>
              <LoadingActiveOutreachAnimation :width="'310px'" :height="'320px'" />
            </div>
          </div>
          <!-- Normal conversation view -->
          <div v-else-if="hasConversations" class="flex h-full flex-col overflow-hidden">
            <!-- `shrink-0` so the sibling below cannot squeeze the header back to its minimum:
                 the header grows when its content wraps, and everything else flows down. -->
            <div
              class="flex min-h-[95px] shrink-0 flex-col justify-between border-b border-b-tint-60"
            >
              <ProHeader ref="proHeaderRef" @change:pro-score="handleProHeaderChangeProScore" />
              <ConversationsTabs :items="tabItems" :disabled="!canNavigateTabs" />
            </div>

            <DoNotContactBanner
              v-if="doNotContact?.active"
              :summary="doNotContact"
              can-undo
              @undo="proHeaderRef?.openRemoveDoNotContact()"
            />
            <InterviewScheduledBanner v-if="nextUpcomingInterview" v-bind="nextUpcomingInterview" />

            <div class="min-h-0 flex-1 overflow-auto">
              <RouterView />
            </div>
          </div>
        </template>
      </section>
    </div>

    <CopilotScoreFeedbackModal />

    <AddProToJobModal
      :excluded-job-ids="addProToJobModalProps.excludedJobIds"
      :open="addProToJobModalProps.isOpen"
      :pros="addProToJobModalProps.pros"
      :show-message-field="addProToJobModalProps.showMessageField"
      @close="onAddProToJobModalClose"
    />
  </div>
</template>
